import { find, keyBy, values } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { ApiResponse, fetchApi } from "../../utils/api";
import {
  CommissionTemplate,
  CommissionType,
  InstallCommission
} from "../../utils/types";
import useCurrency from "../../utils/useCurrency";
import usePrevious from "../../utils/usePrevious";
import { LoadingKey, useLoading } from "../loading/LoadingProvider";
import { useWorkspaces } from "../workspaces";

interface Props {}

export type ContextValue = {
  commissionTemplates: CommissionTemplatesMap;
  fetchCommissionTemplates: () => Promise<void>;
  addCommissionTemplate: (
    commission: CommissionTemplateValues
  ) => Promise<ApiResponse<CommissionTemplate>>;
  updateCommissionTemplate: (
    uuid: string,
    data: CommissionTemplateValues
  ) => Promise<ApiResponse<CommissionTemplate>>;
  removeCommissionTemplate: (uuid: string) => Promise<ApiResponse<[]>>;
  getCommissionValueLabel: (
    installCommission: Pick<InstallCommission, "currency" | "type" | "value">
  ) => string;
  getCommissionTemplateLabel: (
    installCommission: Pick<
      InstallCommission,
      "currency" | "type" | "value" | "template_uuid"
    >
  ) => string;
};

export const CommissionTemplatesContext = React.createContext<
  ContextValue | undefined
>(undefined);

export const CommissionTemplatesProvider: React.FC<Props> = (props) => {
  const { currentWorkspaceId } = useWorkspaces();
  const { addLoader, removeLoader } = useLoading();
  const [commissionTemplates, setComissionsTemplates] = useState({});
  const { getCurrencyTitle } = useCurrency();

  const fetchCommissionTemplates = useCallback(async () => {
    if (!currentWorkspaceId) return;

    addLoader(LoadingKey.workspaceCommissionTemplates);

    try {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/commission-templates`
      );
      const apiResponse: ApiResponse<{
        commissionTemplates: CommissionTemplatesMap;
      }> = await response.json();

      if (!apiResponse.status) return;

      let newCommissionTemplates = {};

      newCommissionTemplates = keyBy(apiResponse.data, "uuid");

      setComissionsTemplates(newCommissionTemplates);
    } catch (error) {
      console.error(error);
    }

    removeLoader(LoadingKey.workspaceCommissionTemplates);
  }, [addLoader, currentWorkspaceId, removeLoader]);

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      setComissionsTemplates({});
    }
  }, [currentWorkspaceId, prevWorkspaceId]);

  useEffect(() => {
    fetchCommissionTemplates();
  }, [fetchCommissionTemplates]);

  const addCommissionTemplate = useCallback(
    async ({
      name,
      currency,
      value,
      type,
      cron_expression,
      primary
    }: CommissionTemplateValues) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/commission-templates`,
        {
          method: "POST",
          body: JSON.stringify({
            name,
            type,
            cron_expression,
            currency,
            primary,
            value
          })
        }
      );

      const apiResponse: ApiResponse<CommissionTemplate> =
        await response.json();

      await fetchCommissionTemplates();

      return apiResponse;
    },
    [currentWorkspaceId, fetchCommissionTemplates]
  );

  const updateCommissionTemplate = useCallback(
    async (
      uuid: string,
      {
        name,
        currency,
        value,
        type,
        cron_expression,
        primary
      }: CommissionTemplateValues
    ) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/commission-templates/${uuid}`,
        {
          method: "PUT",
          body: JSON.stringify({
            name,
            type,
            cron_expression,
            currency,
            primary,
            value
          })
        }
      );

      const apiResponse: ApiResponse<CommissionTemplate> =
        await response.json();

      await fetchCommissionTemplates();

      return apiResponse;
    },
    [currentWorkspaceId, fetchCommissionTemplates]
  );

  const removeCommissionTemplate = useCallback(
    async (uuid: string) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/commission-templates/${uuid}`,
        {
          method: "DELETE"
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      await fetchCommissionTemplates();

      return apiResponse;
    },
    [currentWorkspaceId, fetchCommissionTemplates]
  );

  const getCommissionValueLabel = useCallback(
    ({
      currency,
      type,
      value
    }: Pick<InstallCommission, "currency" | "type" | "value">) => {
      const currencyTitle = getCurrencyTitle(currency);

      switch (type) {
        case CommissionType.percentage:
          return `${value}% ${currencyTitle}`;
        case CommissionType.recurringFixed:
          return `${value} ${currencyTitle} monthly`; // TODO: get frequency from cron expression
        case CommissionType.oneTimeFixed:
          return `${value} ${currencyTitle} one time`;
      }
    },
    [getCurrencyTitle]
  );

  const getCommissionTemplateLabel = useCallback(
    ({
      template_uuid,
      currency,
      value,
      type
    }: Pick<
      InstallCommission,
      "currency" | "type" | "value" | "template_uuid"
    >) => {
      const commissionTemplate = find(values(commissionTemplates), {
        uuid: template_uuid,
        currency,
        value,
        type
      }) as CommissionTemplate | undefined;

      return commissionTemplate?.name || "custom";
    },
    [commissionTemplates]
  );

  const value = useMemo(
    () => ({
      commissionTemplates,
      fetchCommissionTemplates,
      addCommissionTemplate,
      updateCommissionTemplate,
      removeCommissionTemplate,
      getCommissionValueLabel,
      getCommissionTemplateLabel
    }),
    [
      commissionTemplates,
      fetchCommissionTemplates,
      addCommissionTemplate,
      updateCommissionTemplate,
      removeCommissionTemplate,
      getCommissionValueLabel,
      getCommissionTemplateLabel
    ]
  );

  return <CommissionTemplatesContext.Provider value={value} {...props} />;
};

export const useCommissionTemplates = (): ContextValue => {
  const context = useContext(CommissionTemplatesContext);

  if (context === undefined) {
    throw new Error(
      "useCommissionTemplates must be used within an CommissionTemplatesProvider"
    );
  }

  return context;
};

//
// Utils
//

export interface CommissionTemplateValues {
  name: string;
  type: CommissionType;
  cron_expression: string | null;
  value: string;
  currency: string;
  primary: boolean;
}

interface CommissionTemplatesMap {
  [commissionUuid: string]: CommissionTemplate;
}
